import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@apollo/client";
import { UPDATE_EMPLOYEE_DETAILS } from "../../../../../../../apis/organization-apis/people-apis/peopleApis";
import ConfirmSnackbar from "../../../../../../../components/Snackbar/ConfirmSnackbar";
import moment from "moment";
import DatePicker from "../../../../../../../components/DatePicker/DatePicker";

const EditRoleDialog = ({
  open,
  onClose,
  employee,
  currentRole,
  refetch,
  hasHrmsIntegration = false,
}) => {
  const [updateEmployee, { loading: updating }] = useMutation(UPDATE_EMPLOYEE_DETAILS);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [jobTitle, setJobTitle] = useState(currentRole || "");
  const [effectiveDate, setEffectiveDate] = useState(
    employee?.employments?.[0]?.effective_date
      ? moment(employee.employments[0].effective_date).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD")
  );

  useEffect(() => {
    setJobTitle(currentRole || "");
    setEffectiveDate(
      employee?.employments?.[0]?.effective_date
        ? moment(employee.employments[0].effective_date).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD")
    );
  }, [currentRole, employee, open]);

  const handleSave = async () => {
    if (!employee?.id) return;

    try {
      const currentEmployments = employee?.employments || [];
      const existingEmployment = currentEmployments[0];

      const employmentData = {
        ...(existingEmployment?.id && { id: existingEmployment.id }),
        job_title: jobTitle,
        effective_date: effectiveDate,
        employment_type: existingEmployment?.employment_type || "full_time",
      };

      const response = await updateEmployee({
        variables: {
          input: [
            {
              id: employee.id,
              email: employee.work_email,
              employments: [employmentData],
            },
          ],
        },
      });

      if (response?.data?.updateEmployeeDetails?.success) {
        setSnackbarInfo({
          message: "Role updated successfully",
          vertical: "top",
          horizontal: "right",
        });
        if (refetch) {
          await refetch();
        }
        onClose();
      } else {
        setSnackbarInfo({
          message: response?.data?.updateEmployeeDetails?.message || "Failed to update role",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to update role",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            Edit Role
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {hasHrmsIntegration && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Role is managed by HRMS integration and cannot be edited manually.
              </Alert>
            )}
            <TextField
              fullWidth
              label="Job Title"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              disabled={updating || hasHrmsIntegration}
              sx={{ mb: 2 }}
            />
            <DatePicker
              fullWidth
              label="Effective Date"
              value={effectiveDate}
              onChange={(date) => setEffectiveDate(date)}
              disabled={updating || hasHrmsIntegration}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={updating}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updating || !jobTitle.trim() || hasHrmsIntegration}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default EditRoleDialog;

